const LEGACY_CITATION_SEPARATOR = '\u203a';

const citationElement = (element: HTMLElement) =>
    element.matches('a.filing-source-button')
        ? element
        : (element.querySelector<HTMLElement>('a.filing-source-button') ?? element);

const getLegacyCitationParts = (element: HTMLElement) => {
    const source = citationElement(element);
    const company = source.querySelector('.filing-source-company')?.textContent?.trim();
    const filing = source.querySelector('.filing-source-filing')?.textContent?.trim();

    if (company || filing) {
        return { company, filing };
    }

    const [legacyCompany, ...legacyFiling] = (source.textContent ?? '').split(LEGACY_CITATION_SEPARATOR);

    return {
        company: legacyCompany?.trim(),
        filing: legacyFiling.join(LEGACY_CITATION_SEPARATOR).trim(),
    };
};

const getLegacyLogoUrl = (element: HTMLElement) => {
    const logoValue = citationElement(element).style.getPropertyValue('--filing-source-logo').trim();
    const match = logoValue.match(/^url\((['"]?)(.*)\1\)$/);

    return match?.[2] || null;
};

const renderDataAttribute = (name: string, value: unknown) => (value ? { [name]: String(value) } : {});

export const createFilingSourceCitationAttributes = () => ({
    avatar: {
        default: null,
        parseHTML: (element: HTMLElement) => citationElement(element).getAttribute('data-avatar'),
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('data-avatar', attributes.avatar),
    },
    company: {
        default: null,
        parseHTML: (element: HTMLElement) =>
            citationElement(element).getAttribute('data-company') || getLegacyCitationParts(element).company,
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('data-company', attributes.company),
    },
    filing: {
        default: null,
        parseHTML: (element: HTMLElement) =>
            citationElement(element).getAttribute('data-filing') || getLegacyCitationParts(element).filing,
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('data-filing', attributes.filing),
    },
    href: {
        default: null,
        parseHTML: (element: HTMLElement) => citationElement(element).getAttribute('href'),
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('href', attributes.href),
    },
    logoUrl: {
        default: null,
        parseHTML: (element: HTMLElement) =>
            citationElement(element).getAttribute('data-logo-url') || getLegacyLogoUrl(element),
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('data-logo-url', attributes.logoUrl),
    },
    website: {
        default: null,
        parseHTML: (element: HTMLElement) => citationElement(element).getAttribute('data-website'),
        renderHTML: (attributes: Record<string, unknown>) => renderDataAttribute('data-website', attributes.website),
    },
});

export const filingSourceCitationContent = (company: string, filing: string) => [
    ['span', { class: 'filing-source-company' }, company],
    ['span', { 'aria-hidden': 'true', class: 'filing-source-chevron' }],
    ['span', { class: 'filing-source-filing' }, filing],
];
